import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Icon } from "./Icon.js";

/**
 * Scrolls a card that just opened into view, whole: clear of the top bar and
 * the phone's tab bar (the `.panel-reveal` scroll margins), not with its top
 * edge sitting just under the bottom of the screen. A card taller than the
 * screen lines its top up instead, so the heading is what shows first.
 */
export function revealPanel(element: Element | null) {
  if (!element) return;
  const style = getComputedStyle(element);
  const margins = parseFloat(style.scrollMarginTop) + parseFloat(style.scrollMarginBottom);
  const fits = element.getBoundingClientRect().height + margins <= window.innerHeight;
  const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  element.scrollIntoView({ behavior: still ? "auto" : "smooth", block: fits ? "nearest" : "start" });
}

/**
 * A card: the app's one content container. Solid rather than glass since the
 * v2 pass (docs/notes/theming.md): translucency is for chrome that floats over
 * content, and a card *is* the content.
 *
 * The heading is optional, and so is a line under it. Most cards need neither:
 * a card whose controls already say what it is for does not need a sentence
 * repeating that. `actions` sits at the heading's right, for the one or two
 * buttons that act on the whole card.
 *
 * `collapsible` turns the heading into a button that folds the body away, for
 * the long item screen where somebody is only after one card. The body stays
 * mounted while folded, so a half-typed field is not lost by tidying up.
 *
 * `reveal` is for a card that opens in answer to a button somewhere else on
 * the page (Deploy, Return, Retire): it scrolls itself into view on mount.
 *
 * The optional props admit `undefined` explicitly because
 * `exactOptionalPropertyTypes` is on, and `note={x ? "..." : undefined}` is
 * the natural way to write a conditional one.
 */
export function Panel({
  title,
  note,
  actions,
  className,
  collapsible,
  reveal,
  children,
}: {
  title?: string | undefined;
  note?: string | undefined;
  actions?: ReactNode;
  className?: string | undefined;
  /** Needs a `title`: it is what the toggle is labelled with. */
  collapsible?: boolean | undefined;
  reveal?: boolean | undefined;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(true);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (reveal) revealPanel(ref.current);
    // On mount only: the card opened, so bring it into view once.
  }, []);
  const bodyId = useId();
  const foldable = collapsible === true && Boolean(title);
  const folded = foldable && !open;

  const heading = title && <h2 className="panel-title">{title}</h2>;

  return (
    <section ref={ref} className={["panel", reveal && "panel-reveal", className].filter(Boolean).join(" ")}>
      {(title || actions) && (
        <header className={folded ? "panel-head panel-head-folded" : "panel-head"}>
          <div className="panel-heading">
            {foldable ? (
              <button
                type="button"
                className="panel-toggle"
                aria-expanded={open}
                aria-controls={bodyId}
                onClick={() => setOpen(!open)}
              >
                <Icon name={open ? "chevron-down" : "chevron-right"} size={18} />
                {heading}
              </button>
            ) : (
              heading
            )}
            {note && <p className="panel-note">{note}</p>}
          </div>
          {actions && <div className="panel-actions">{actions}</div>}
        </header>
      )}
      {!title && !actions && note && <p className="panel-note">{note}</p>}
      {foldable ? (
        <div id={bodyId} hidden={folded}>
          {children}
        </div>
      ) : (
        children
      )}
    </section>
  );
}
